import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import type { DirectMessagePayload } from '@nbh/shared';
import { api } from '../../lib/api';
import { cacheFirst } from '../../lib/cacheFirst';
import { cacheDirectThread, getCachedDirectThread } from '../../lib/db';
import { useAuth } from '../../auth/AuthProvider';
import { useDirectMessages } from '../../hooks/useDirectMessages';
import { ChatComposer } from '../chat/ChatComposer';
import { ChatThread } from '../chat/ChatThread';
import { InitialsAvatar } from '../chat/ChatListRow';
import type { ThreadMessage } from '../chat/threadModel';

/**
 * One conversation (decision D29).
 *
 * The composer is pinned to the bottom, in thumb reach — this screen is used
 * one-handed while walking, like the rest of the frontline surface. The thread body is
 * the shared `ChatThread` in its 1:1 context: no sender names, no system rows.
 *
 * Reads the cache before the network (Rule 4), so a conversation opened once is
 * readable with no signal. Sending still needs the network and says so.
 */
export function MessageThread({
  conversationId,
  counterpartId,
  counterpartName,
  onBack,
}: {
  conversationId: string;
  counterpartId: string;
  counterpartName: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState<DirectMessagePayload[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setError(null);
    cancelRef.current?.();
    cancelRef.current = cacheFirst<DirectMessagePayload[]>({
      readCache: () => getCachedDirectThread(conversationId),
      load: () => api.listDirectMessages(conversationId),
      writeCache: (rows) => cacheDirectThread(conversationId, rows),
      onValue: setMessages,
      onError: (caught) => {
        setMessages((current) => current ?? []);
        setError(caught.message);
      },
    });
    return () => cancelRef.current?.();
  }, [conversationId]);

  /**
   * Appends only what belongs to this thread, and only if it is new.
   *
   * The sender receives their own broadcast, so a message posted from this device
   * arrives twice — once as the response, once over the socket. Keyed on id rather
   * than suppressed by author, because the same account may be signed in on a second
   * device whose copy is the one that arrived first.
   */
  const receive = useCallback(
    (incoming: DirectMessagePayload) => {
      if (incoming.conversationId !== conversationId) return;
      setMessages((current) => {
        const known = current ?? [];
        if (known.some((m) => m.id === incoming.id)) return current;
        const next = [...known, incoming];
        void cacheDirectThread(conversationId, next);
        return next;
      });
    },
    [conversationId],
  );

  useDirectMessages(receive);

  // Follows the conversation down as it grows, which is what a chat is expected to do.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const body = draft.trim();
    if (!body || busy) return;

    setBusy(true);
    setError(null);
    try {
      receive(await api.sendDirectMessage(conversationId, body));
      setDraft('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('messages.sendFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    // flex-1 within AppShell's main, not min-h-dvh: a full viewport height *plus* the
    // padding that main reserves for the tab bar puts the composer below the fold, off
    // the bottom of the screen. min-h-0 lets the message list shrink so the composer
    // stays put rather than being pushed down by a long conversation.
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-2 border-b border-slate-800 px-2 py-2">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('messages.back')}
          className="flex size-12 min-h-0 items-center justify-center bg-transparent p-0 text-slate-400"
        >
          <ChevronLeft className="size-5" aria-hidden="true" />
        </button>
        <InitialsAvatar id={counterpartId} name={counterpartName} />
        <p className="truncate text-sm font-semibold text-slate-100">{counterpartName}</p>
      </header>

      <div className="flex-1 overflow-y-auto">
        {error && (messages === null || messages.length === 0) && (
          <p role="alert" className="px-4 pt-3 text-sm text-red-400">
            {error}
          </p>
        )}
        {messages === null ? (
          <p className="p-4 text-sm text-slate-400">{t('messages.loading')}</p>
        ) : (
          <ChatThread
            messages={messages.map(toThreadMessage)}
            viewerId={user?.id}
            multiParty={false}
            emptyText={t('messages.emptyThread')}
          />
        )}
        <div ref={bottom} />
      </div>

      {error && messages !== null && messages.length > 0 && (
        <p role="alert" className="px-3 pb-2 text-sm text-red-400">
          {error}
        </p>
      )}

      <ChatComposer
        draft={draft}
        onDraft={setDraft}
        onSubmit={submit}
        busy={busy}
        maxLength={4000}
        placeholder={t('messages.placeholder')}
        sendLabel={t('messages.send')}
      />
    </div>
  );
}

function toThreadMessage(message: DirectMessagePayload): ThreadMessage {
  return {
    id: message.id,
    body: message.body,
    senderId: message.senderId,
    senderName: message.senderName,
    createdAt: message.createdAt,
  };
}
